import { useMutation, useQueryClient } from '@tanstack/react-query';
import PathwayService from '@/services/PathwayService';
import type { ResolvePathwayInput, ApplyPathwayInput } from '@/services/PathwayService';
import CarePlanService from '@/services/CarePlanService';
import type { CarePlan } from '@/services/CarePlanService';
import { readAccessSubtrack } from '@/logic/onboardingReview';
import { CARE_PLAN_KEYS } from '@/state/data/carePlan/keys';
import { scheduleInvalidate, WF_RECONCILE_DELAY_MS } from '@/state/data/_shared/wf-race';

// resolve = preview-only, no DB write → no race.
export function useResolveCarePathway() {
  return useMutation({
    mutationFn: (input: ResolvePathwayInput) => PathwayService.resolveCarePathway(input),
  });
}

export const APPLY_CONFIRM_POLL_MS = 2000;
export const APPLY_CONFIRM_TIMEOUT_MS = 30000;
// Late backstop reconcile when the poll gave up — WF15 may still land after the cap.
export const APPLY_LATE_RECONCILE_MS = WF_RECONCILE_DELAY_MS * 5;

export interface ApplyPathwayOutcome {
  confirmed: boolean;
  plan: CarePlan | null;
}

export interface ConfirmPathwayOpts {
  withingsUserId: string | number;
  selectedId: string | null;
  baseline: CarePlan | null;
  fetchPlan?: (id: string | number) => Promise<CarePlan | null>;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  pollMs?: number;
  timeoutMs?: number;
}

// Poll until the WF36→WF15 plan write lands. Version bump = primary signal (every
// WF15 write increments it — confirms even a same-subtrack re-apply); subtrack match
// = fallback for version-less rows. A concurrent unrelated plan write also bumps
// version (rare false-confirm — self-corrects on the refetch that follows).
export async function confirmPathwayApplied({
  withingsUserId,
  selectedId,
  baseline,
  fetchPlan = CarePlanService.getCarePlanByUser,
  sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
  now = () => Date.now(),
  pollMs = APPLY_CONFIRM_POLL_MS,
  timeoutMs = APPLY_CONFIRM_TIMEOUT_MS,
}: ConfirmPathwayOpts): Promise<ApplyPathwayOutcome> {
  const baselineVersion = Number(baseline?.version ?? 0);
  const baselineSubtrack = readAccessSubtrack(baseline)?.subtrack_id ?? null;
  const subtrackChanges = !!selectedId && String(selectedId) !== String(baselineSubtrack ?? '');
  const deadline = now() + timeoutMs;

  const poll = async (): Promise<ApplyPathwayOutcome> => {
    if (now() >= deadline) return { confirmed: false, plan: null };
    await sleep(pollMs);
    // A transient read failure is not a verdict — keep polling until the deadline.
    const fresh = await fetchPlan(withingsUserId).catch(() => null);
    if (fresh) {
      const versionBumped = Number(fresh.version ?? 0) > baselineVersion;
      const subtrackLanded = subtrackChanges
        && String(readAccessSubtrack(fresh)?.subtrack_id ?? '') === String(selectedId);
      if (versionBumped || subtrackLanded) return { confirmed: true, plan: fresh };
    }
    return poll();
  };
  return poll();
}

// apply = Tier 4 pathway_apply_requested → n8n (fire-and-forward: apiv2 acks
// before WF36/WF15 run). The mutation resolves only once the plan write is
// CONFIRMED by polling, or after the 30s cap with confirmed:false — it never
// rejects on a timeout (the workflow may still land later).
export function useApplyCarePathway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApplyPathwayInput): Promise<ApplyPathwayOutcome> => {
      let baseline: CarePlan | null = null;
      try {
        baseline = await CarePlanService.getCarePlanByUser(input.withingsUserId);
      } catch {
        // Degraded baseline → the first successful poll read confirms. Same net
        // effect as today's blind refetch; never block the apply on a read.
      }
      await PathwayService.applyCarePathway(input);
      return confirmPathwayApplied({
        withingsUserId: input.withingsUserId,
        selectedId: input.selected?.[0] != null ? String(input.selected[0]) : null,
        baseline,
      });
    },
    onSettled: (outcome, _e, vars) => {
      if (outcome?.confirmed) {
        qc.invalidateQueries({ queryKey: CARE_PLAN_KEYS.byUser(vars.withingsUserId) });
      } else {
        // Unconfirmed/error backstop — reconcile once more in case WF15 lands late.
        scheduleInvalidate(qc, CARE_PLAN_KEYS.byUser(vars.withingsUserId), APPLY_LATE_RECONCILE_MS);
      }
    },
  });
}
